import SearchIcon from '@mui/icons-material/Search';
import { Box, InputAdornment, MenuItem, Paper, Stack, Tab, Tabs, TextField } from '@mui/material';
import React, { useMemo, useState } from 'react';
import type { LedgerType } from '../../data/types';
import { dateTime } from '../../lib/format';
import { LEDGER_LABELS } from '../../lib/labels';
import { useTenantScope } from '../../store/selectors';
import DataTable from '../../ui/DataTable';
import { AboutThisPage, PageHeader } from '../../ui/primitives';
import LedgerTable from '../common/LedgerTable';

export default function AuditPage() {
  const scope = useTenantScope();
  const [tab, setTab] = useState(0);
  const [type, setType] = useState<'ALL' | LedgerType>('ALL');
  const [search, setSearch] = useState('');
  const entries = useMemo(
    () => scope.ledger.filter((entry) => type === 'ALL' || entry.type === type),
    [scope.ledger, type],
  );
  const events = useMemo(() => {
    const term = search.trim().toLowerCase();
    return scope.audit.filter(
      (event) =>
        !term ||
        `${event.actor} ${event.action} ${event.target} ${event.detail}`
          .toLowerCase()
          .includes(term),
    );
  }, [scope.audit, search]);
  return (
    <>
      <PageHeader
        title="Ledger & audit"
        subtitle="The append-only registry ledger and the audit trail of every user action."
      />
      <AboutThisPage>
        <p>
          Ledger entries are never edited or deleted. A mistake is corrected by a reversing entry,
          which is itself approved. Each entry records who posted it, who approved it and the event
          it came from. The audit trail covers everything else, including views of sensitive data
          such as TFNs.
        </p>
      </AboutThisPage>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(_event, next) => setTab(next)}>
          <Tab label={`Ledger (${scope.ledger.length})`} />
          <Tab label={`Audit trail (${scope.audit.length})`} />
        </Tabs>
      </Box>
      {tab === 0 && (
        <Paper variant="outlined">
          <Stack direction="row" spacing={1.5} sx={{ p: 2 }}>
            <TextField
              select
              size="small"
              label="Entry type"
              value={type}
              onChange={(event) => setType(event.target.value as typeof type)}
              sx={{ minWidth: 240 }}
            >
              <MenuItem value="ALL">All types</MenuItem>
              {(Object.keys(LEDGER_LABELS) as LedgerType[]).map((key) => (
                <MenuItem key={key} value={key}>
                  {LEDGER_LABELS[key]}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <LedgerTable entries={entries} pageSize={30} />
        </Paper>
      )}
      {tab === 1 && (
        <Paper variant="outlined">
          <Box sx={{ p: 2 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search actor, action or record"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <DataTable
            rows={events}
            rowKey={(row) => row.id}
            dense
            pageSize={30}
            defaultSort={{ key: 'at', direction: 'desc' }}
            columns={[
              {
                key: 'at',
                label: 'When',
                sortValue: (row) => row.at,
                render: (row) => dateTime(row.at),
              },
              {
                key: 'actor',
                label: 'Who',
                sortValue: (row) => row.actor,
                render: (row) => row.actor,
              },
              { key: 'action', label: 'Action', render: (row) => row.action },
              { key: 'target', label: 'Record', render: (row) => row.target },
              { key: 'detail', label: 'Detail', hideBelow: 'md', render: (row) => row.detail },
            ]}
          />
        </Paper>
      )}
    </>
  );
}
